import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as HeartIcon } from '@repo/common/assets/icons/32px/icon_32px_heart_filled.svg';
import { ReactComponent as MobileHeartIcon } from '@repo/common/assets/icons/24px/icon_24px_heart_filled.svg';
import { removeWorkshopLike } from '../../../../api/workshop/workshopLike.api';
import {
  Chip,
  formatDateToMonthDayYear,
  useIsMobile,
  useT,
} from '@repo/common';

const LABEL_KR = {
  Waitlist: '대기',
  Upcoming: '예정',
  Attended: '참석',
  Absent: '불참',
};

const LikedWorkshopCard = ({ item, onUnlike }) => {
  const t = useT();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isOverCancel, setIsOverCancel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageUrl = item.imageUrl;
  const bookingStatus = item.bookingStatus;
  const workshopTitle = item.title;
  const workshopDate = item.date;
  const workshopAddress = item.meetingPointAddress;
  const isWaitlist = item.bookingStatus === 'WAITLISTED';

  const shouldHideChip =
    !bookingStatus || !['WAITLISTED', 'CONFIRMED'].includes(bookingStatus);

  const label = (() => {
    if (isWaitlist) {
      return 'Waitlist';
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    if (item.date > todayStr) {
      return 'Upcoming';
    }

    return 'Attended';
  })();

  const showLabel = label === 'Attended' || label === 'Absent';
  const labelTone =
    label === 'Attended'
      ? { bg: 'bg-green-light02', text: 'text-green-deep04' }
      : { bg: 'bg-grayscale-gray02', text: 'text-text-placeholder' };

  const handleUnlikeClick = async (e) => {
    e.stopPropagation();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      await removeWorkshopLike(item.workshopId);

      if (onUnlike) {
        onUnlike(item.workshopId);
      }
    } catch (error) {
      console.error('좋아요 취소 실패:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`flex items-center p-4 gap-2 tabletH:py-6 tabletH:pl-6 tabletH:pr-8 tabletH:gap-6 w-full bg-grayscale-gray00 rounded-[15px] overflow-hidden cursor-pointer`}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
      onClick={() =>
        navigate(`/community/kpathport/workshop/${item.workshopId}`)
      }
    >
      <img
        src={imageUrl}
        alt={workshopTitle}
        className='size-[52px] min-w-[52px] min-h-[52px] tabletH:size-[70px] tabletH:min-w-[70px] tabletH:min-h-[70px] rounded-lg object-cover'
      />

      <div className='flex flex-col justify-between w-full'>
        <div className='flex justify-between items-center gap-2 w-full h-9'>
          <div className='flex flex-col items-start gap-1'>
            <div className='flex gap-1 tabletH:gap-2'>
              {showLabel && (
                <Chip
                  type='fill'
                  state='disabled'
                  size='xs'
                  hideIcon
                  disableTypography
                  disableDefaultColors
                  bgclass={`h-auto rounded-md px-1 py-[1px] tabletH:px-1.5 tabletH:py-0.5 ${labelTone.bg}`}
                  borderclass='ring-0'
                  textclass={`typo-b11Strong tabletH:typo-b12Strong ${labelTone.text}`}
                >
                  {t(label, LABEL_KR[label])}
                </Chip>
              )}
              <div className='text-grayscale-gray05 typo-b12 tabletH:typo-b14Strong'>
                {workshopAddress} · {formatDateToMonthDayYear(workshopDate)}
              </div>
            </div>
            <div className='typo-b16Strong tabletH:typo-b20Strong text-grayscale-gray08 line-clamp-1'>
              {workshopTitle}
            </div>
          </div>

          <div
            onMouseEnter={() => setIsOverCancel(true)}
            onMouseLeave={() => setIsOverCancel(false)}
            onClick={handleUnlikeClick}
            className={`flex-shrink-0 cursor-pointer p-0.5 tabletH:p-[3px] transition-opacity ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isMobile ? (
              <div className='size-6 text-functional-failed03'>
                <MobileHeartIcon />
              </div>
            ) : (
              <div className='size-8 text-functional-failed03'>
                <HeartIcon />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LikedWorkshopCard;
